import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Books, UploadSimple, MagnifyingGlass, ChatCircleDots, Tag, FolderSimple,
  Trash, ArrowClockwise, FilePdf, FileDoc, FileText, Files, Sparkle,
  Eye, X, Plus, CaretRight, Hourglass, ArrowsLeftRight, CheckSquare, Square,
  Tray, MagicWand, Check,
} from "@phosphor-icons/react";

const FOLDER_DEFS = [
  { k: "sin_clasificar", label: "Sin clasificar", icon: Tray },
  { k: "polizas", label: "Pólizas", icon: FileText },
  { k: "circulares", label: "Circulares", icon: Books },
  { k: "instrucciones_tecnicas", label: "Instrucciones técnicas", icon: FolderSimple },
  { k: "modelos", label: "Modelos / Plantillas", icon: FileDoc },
  { k: "baremos", label: "Baremos / Tarifas", icon: FilePdf },
  { k: "otros", label: "Otros", icon: Files },
];

const MAX_BULK_FILES = 20;

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const IconForFile = ({ filename }) => {
  const ext = (filename || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return <FilePdf className="w-5 h-5 text-rose-600" weight="fill" />;
  if (ext === "docx") return <FileDoc className="w-5 h-5 text-sky-700" weight="fill" />;
  return <FileText className="w-5 h-5 text-slate-600" weight="fill" />;
};

export default function RepositorioTab({ aseguradoraId, aseguradoraName, canEdit }) {
  const [data, setData] = useState({ docs: [], folders: [], tags: [], folder_options: {} });
  const [filterFolder, setFilterFolder] = useState(null);
  const [filterTag, setFilterTag] = useState(null);
  const [textFilter, setTextFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("docs"); // docs | search | chat
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Selección múltiple para mover/categorizar en lote
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkMoving, setBulkMoving] = useState(false);

  const toggleSel = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    const ids = data.docs.map((d) => d.id);
    setSelectedIds((prev) => {
      // Si todos los visibles ya están seleccionados → toggle off (deselecciona)
      if (ids.every((id) => prev.has(id)) && ids.length > 0) return new Set();
      return new Set([...prev, ...ids]);
    });
  };
  const clearSel = () => setSelectedIds(new Set());

  const bulkMoveTo = async (folderKey) => {
    if (selectedIds.size === 0) return;
    setBulkMoving(true);
    try {
      const r = await api.post(
        `/aseguradoras/${aseguradoraId}/repositorio/bulk-move`,
        { doc_ids: [...selectedIds], folder: folderKey },
      );
      toast.success(`${r.data.moved_count} documento(s) movidos a "${
        FOLDER_DEFS.find((f) => f.k === folderKey)?.label || folderKey}"`);
      clearSel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al mover");
    } finally {
      setBulkMoving(false);
    }
  };

  // Aplicar sugerencias IA a un único doc (botón "Aceptar" en la card)
  const acceptSuggestion = async (doc) => {
    if (!doc.suggested_folder) return;
    try {
      await api.post(
        `/aseguradoras/${aseguradoraId}/repositorio/suggestions/apply`,
        { doc_ids: [doc.id] },
      );
      toast.success(`Movido a "${
        FOLDER_DEFS.find((f) => f.k === doc.suggested_folder)?.label || doc.suggested_folder}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar sugerencia");
    }
  };

  const dismissSuggestion = async (doc) => {
    try {
      await api.post(
        `/aseguradoras/${aseguradoraId}/repositorio/suggestions/dismiss`,
        { doc_ids: [doc.id] },
      );
      load();
    } catch (e) {
      toast.error("Error al descartar");
    }
  };

  // Aplicar TODAS las sugerencias visibles (banner superior)
  const applyAllSuggestions = async () => {
    const ids = data.docs.filter((d) => d.suggested_folder && d.folder === "sin_clasificar").map((d) => d.id);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Aceptar las ${ids.length} sugerencia(s) IA y mover esos documentos a la carpeta propuesta?`)) return;
    try {
      const r = await api.post(
        `/aseguradoras/${aseguradoraId}/repositorio/suggestions/apply`,
        { doc_ids: ids },
      );
      toast.success(`${r.data.applied_count} documento(s) re-categorizado(s) por la IA`);
      load();
    } catch (e) {
      toast.error("Error aplicando sugerencias");
    }
  };

  // Cuantos docs visibles tienen sugerencia pendiente
  const pendingSuggestions = useMemo(
    () => data.docs.filter((d) => d.suggested_folder && d.folder === "sin_clasificar").length,
    [data.docs],
  );

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterFolder) params.set("folder", filterFolder);
      if (filterTag) params.set("tag", filterTag);
      if (textFilter) params.set("q", textFilter);
      const { data: res } = await api.get(
        `/aseguradoras/${aseguradoraId}/repositorio?${params.toString()}`,
      );
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando repositorio");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [aseguradoraId, filterFolder, filterTag]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce búsqueda de texto
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 400);
    return () => clearTimeout(t);
  }, [textFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (doc) => {
    if (!window.confirm(`¿Eliminar "${doc.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/aseguradoras/${aseguradoraId}/repositorio/${doc.id}`);
      toast.success("Documento eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al eliminar"); }
  };

  const handleReindex = async (doc) => {
    try {
      await api.post(`/aseguradoras/${aseguradoraId}/repositorio/${doc.id}/reindex`);
      toast.success("Re-indexando…");
      setTimeout(load, 1500);
    } catch (e) { toast.error("Error al reindexar"); }
  };

  return (
    <div className="space-y-4" data-testid="repositorio-tab">
      {/* Header con acciones y tabs internas */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-[12px] font-semibold">
          <button onClick={() => setView("docs")} className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${view === "docs" ? "bg-foreground text-white" : "bg-white border border-border text-muted hover:text-foreground"}`} data-testid="repo-view-docs">
            <Books className="w-4 h-4" weight="fill" /> Documentos
          </button>
          <button onClick={() => setView("search")} className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${view === "search" ? "bg-amber-600 text-white" : "bg-white border border-border text-muted hover:text-foreground"}`} data-testid="repo-view-search">
            <Sparkle className="w-4 h-4" weight="fill" /> Búsqueda IA
          </button>
          <button onClick={() => setView("chat")} className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${view === "chat" ? "bg-amber-600 text-white" : "bg-white border border-border text-muted hover:text-foreground"}`} data-testid="repo-view-chat">
            <ChatCircleDots className="w-4 h-4" weight="fill" /> Chat con docs
          </button>
        </div>
        {canEdit && view === "docs" && (
          <button onClick={() => setUploadOpen(true)} data-testid="repo-upload-btn" className="ml-auto btn-brand text-[12px] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md font-semibold">
            <UploadSimple className="w-4 h-4" weight="bold" /> Subir documentos
          </button>
        )}
      </div>

      {view === "docs" && (
        <div className="grid grid-cols-[220px_1fr] gap-4">
          {/* Sidebar carpetas + tags */}
          <aside className="space-y-3">
            <div className="bg-white border border-border rounded-md p-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted px-2 py-1">Carpetas</div>
              <button onClick={() => setFilterFolder(null)} className={`w-full text-left px-2 py-1.5 text-[12px] rounded ${!filterFolder ? "bg-amber-50 text-amber-900 font-semibold" : "hover:bg-slate-50"}`} data-testid="repo-folder-all">
                Todos {data.docs.length > 0 && <span className="text-muted">({data.folders.reduce((a, f) => a + f.count, 0)})</span>}
              </button>
              {FOLDER_DEFS.map((f) => {
                const found = data.folders.find((x) => x.key === f.k);
                const count = found?.count || 0;
                return (
                  <button
                    key={f.k}
                    onClick={() => setFilterFolder(filterFolder === f.k ? null : f.k)}
                    data-testid={`repo-folder-${f.k}`}
                    className={`w-full text-left px-2 py-1.5 text-[12px] rounded inline-flex items-center gap-1.5 ${filterFolder === f.k ? "bg-amber-50 text-amber-900 font-semibold" : "hover:bg-slate-50"}`}
                  >
                    <f.icon className="w-3.5 h-3.5 flex-shrink-0" weight="fill" />
                    <span className="flex-1 truncate">{f.label}</span>
                    {count > 0 && <span className="text-muted text-[10px]">{count}</span>}
                  </button>
                );
              })}
            </div>
            {data.tags.length > 0 && (
              <div className="bg-white border border-border rounded-md p-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted px-2 py-1">Etiquetas</div>
                <div className="flex flex-wrap gap-1 px-1 py-1">
                  {data.tags.slice(0, 30).map((t) => (
                    <button
                      key={t.name}
                      onClick={() => setFilterTag(filterTag === t.name ? null : t.name)}
                      data-testid={`repo-tag-${t.name}`}
                      className={`text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 border ${filterTag === t.name ? "bg-amber-600 text-white border-amber-700" : "bg-slate-50 text-slate-700 border-slate-200 hover:border-amber-400"}`}
                    >
                      <Tag className="w-2.5 h-2.5" weight="fill" />
                      {t.name} <span className="text-muted">{t.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>

          {/* Lista de documentos */}
          <div className="space-y-3 min-w-0">
            <div className="relative">
              <input
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="Buscar por título, nombre de fichero o resumen…"
                data-testid="repo-text-filter"
                className="w-full pl-8 pr-3 py-2 text-[12px] border border-border rounded-md bg-white"
              />
              <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2.5 text-muted" />
            </div>

            {loading ? (
              <div className="text-center text-muted text-sm py-12">Cargando…</div>
            ) : data.docs.length === 0 ? (
              <div className="bg-white border border-dashed border-border rounded-md p-8 text-center">
                <Books className="w-10 h-10 text-muted mx-auto mb-2" weight="duotone" />
                <div className="text-sm text-muted">
                  {filterFolder || filterTag || textFilter
                    ? "Ningún documento coincide con los filtros."
                    : `Aún no hay documentos en el repositorio de ${aseguradoraName || "esta aseguradora"}.`}
                </div>
                {canEdit && !filterFolder && !filterTag && !textFilter && (
                  <button onClick={() => setUploadOpen(true)} className="mt-3 btn-brand text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold">
                    <UploadSimple className="w-3.5 h-3.5" weight="bold" /> Sube los primeros
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Banner cuando hay sugerencias IA pendientes */}
                {canEdit && pendingSuggestions > 0 && (
                  <SuggestionsBanner
                    count={pendingSuggestions}
                    onApplyAll={applyAllSuggestions}
                  />
                )}
                {/* Cabecera con selección + barra de acciones masivas */}
                {canEdit && (
                  <BulkActionsBar
                    docs={data.docs}
                    selectedIds={selectedIds}
                    onToggleAll={selectAllVisible}
                    onClear={clearSel}
                    onMove={bulkMoveTo}
                    moving={bulkMoving}
                  />
                )}
                <div className="space-y-2">
                  {data.docs.map((d) => (
                    <DocCard
                      key={d.id}
                      doc={d}
                      aseguradoraId={aseguradoraId}
                      onDelete={() => handleDelete(d)}
                      onReindex={() => handleReindex(d)}
                      onEdit={() => setEditing(d)}
                      canEdit={canEdit}
                      selected={selectedIds.has(d.id)}
                      onToggleSelect={() => toggleSel(d.id)}
                      showSelector={canEdit}
                      onAcceptSuggestion={() => acceptSuggestion(d)}
                      onDismissSuggestion={() => dismissSuggestion(d)}
                      folderDefs={FOLDER_DEFS}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {view === "search" && (
        <SearchPanel aseguradoraId={aseguradoraId} folders={FOLDER_DEFS} folderOptions={data.folder_options} />
      )}

      {view === "chat" && (
        <ChatPanel aseguradoraId={aseguradoraId} aseguradoraName={aseguradoraName} folders={FOLDER_DEFS} />
      )}

      {uploadOpen && (
        <BulkUploadModal
          aseguradoraId={aseguradoraId}
          defaultFolder={filterFolder || "sin_clasificar"}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); load(); }}
        />
      )}
      {editing && (
        <EditModal
          aseguradoraId={aseguradoraId}
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function StatusPill({ doc }) {
  const map = {
    pending: ["Pendiente", "bg-slate-100 text-slate-700"],
    running: ["Procesando", "bg-amber-50 text-amber-800"],
    ready: ["Listo", "bg-emerald-50 text-emerald-700"],
    error: ["Error", "bg-rose-50 text-rose-700"],
  };
  const [label, cls] = map[doc.index_status] || map.pending;
  return (
    <span className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${cls}`} title={doc.index_error || ""}>
      {doc.index_status === "running" && <Hourglass className="w-2.5 h-2.5" weight="fill" />}
      {label}
      {doc.n_chunks > 0 && <span className="opacity-60">· {doc.n_chunks} chunks</span>}
    </span>
  );
}

function SuggestionsBanner({ count, onApplyAll }) {
  return (
    <div className="bg-gradient-to-r from-purple-50 to-amber-50 border border-purple-200 rounded-md p-3 flex items-center gap-3"
         data-testid="repo-suggestions-banner">
      <span className="icon-chip icon-chip-purple !w-8 !h-8 flex-shrink-0">
        <MagicWand className="w-4 h-4" weight="fill" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13px] text-foreground">
          La IA ha clasificado {count} documento{count === 1 ? "" : "s"} de "Sin clasificar"
        </div>
        <div className="text-[11px] text-muted">
          Revisa la sugerencia en cada tarjeta o acepta todas de golpe.
        </div>
      </div>
      <button onClick={onApplyAll}
              data-testid="repo-apply-all-suggestions"
              className="bg-purple-600 hover:bg-purple-700 text-white text-[11.5px] h-9 px-3.5 rounded-md font-semibold inline-flex items-center gap-1.5">
        <MagicWand className="w-3.5 h-3.5" weight="fill" /> Aceptar todas
      </button>
    </div>
  );
}

function DocCard({ doc, aseguradoraId, onDelete, onReindex, onEdit, canEdit,
                     selected = false, onToggleSelect, showSelector = false,
                     onAcceptSuggestion, onDismissSuggestion, folderDefs = [] }) {
  const [open, setOpen] = useState(false);
  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/aseguradoras/${aseguradoraId}/repositorio/${doc.id}/file`;
  return (
    <div className={`bg-white border rounded-md transition-colors ${
            selected ? "border-amber-500 ring-1 ring-amber-300" : "border-border hover:border-amber-300"
          }`}
         data-testid={`repo-doc-${doc.id}`}>
      <div className="flex items-start gap-3 p-3">
        {showSelector && (
          <button onClick={onToggleSelect}
                  data-testid={`repo-doc-select-${doc.id}`}
                  className="flex-shrink-0 mt-0.5 text-amber-600 hover:text-amber-700"
                  aria-label={selected ? "Deseleccionar" : "Seleccionar"}>
            {selected ? <CheckSquare className="w-5 h-5" weight="fill" /> : <Square className="w-5 h-5" weight="regular" />}
          </button>
        )}
        <div className="flex-shrink-0 mt-0.5"><IconForFile filename={doc.filename} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <button onClick={() => setOpen((v) => !v)} className="font-semibold text-[13px] text-foreground hover:text-amber-700 text-left truncate" data-testid={`repo-doc-title-${doc.id}`}>
              {doc.title}
            </button>
            <StatusPill doc={doc} />
            {(doc.tags || []).map((t) => (
              <span key={t} className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                <Tag className="w-2.5 h-2.5" weight="fill" /> {t}
              </span>
            ))}
          </div>
          <div className="text-[11px] text-muted mt-0.5 truncate">
            {doc.filename} · {fmtSize(doc.size_bytes)} · {doc.n_pages} pág.
            {doc.subfolder && <span> · 📁 {doc.subfolder}</span>}
          </div>
          {/* Sugerencia IA — sólo si el doc sigue en sin_clasificar */}
          {doc.folder === "sin_clasificar" && doc.suggested_folder && canEdit && (
            <SuggestionRibbon
              doc={doc}
              folderDefs={folderDefs}
              onAccept={onAcceptSuggestion}
              onDismiss={onDismissSuggestion}
            />
          )}
          {open && doc.summary && (
            <div className="mt-2 p-2.5 bg-amber-50/50 border border-amber-200 rounded text-[11.5px] text-foreground whitespace-pre-line leading-relaxed">
              <div className="text-[9px] uppercase font-bold tracking-widest text-amber-700 mb-1 inline-flex items-center gap-1">
                <Sparkle className="w-2.5 h-2.5" weight="fill" /> Resumen IA
              </div>
              {doc.summary}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <a href={fileUrl} target="_blank" rel="noreferrer" title="Ver archivo" data-testid={`repo-doc-view-${doc.id}`} className="text-muted hover:text-foreground p-1.5">
            <Eye className="w-4 h-4" weight="bold" />
          </a>
          {canEdit && (
            <>
              <button onClick={onEdit} title="Editar" className="text-muted hover:text-foreground p-1.5" data-testid={`repo-doc-edit-${doc.id}`}>
                <Plus className="w-4 h-4 rotate-45" weight="bold" />
              </button>
              <button onClick={onReindex} title="Re-indexar" className="text-muted hover:text-foreground p-1.5" data-testid={`repo-doc-reindex-${doc.id}`}>
                <ArrowClockwise className="w-4 h-4" weight="bold" />
              </button>
              <button onClick={onDelete} title="Eliminar" className="text-muted hover:text-rose-600 p-1.5" data-testid={`repo-doc-delete-${doc.id}`}>
                <Trash className="w-4 h-4" weight="bold" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SuggestionRibbon({ doc, folderDefs, onAccept, onDismiss }) {
  const target = folderDefs.find((f) => f.k === doc.suggested_folder);
  const conf = Math.round((doc.suggested_confidence || 0) * 100);
  const confColor = conf >= 80 ? "text-emerald-700" : conf >= 60 ? "text-amber-700" : "text-slate-600";
  return (
    <div className="mt-2 flex items-start gap-2 p-2 rounded-md bg-purple-50/60 border border-purple-200"
         data-testid={`repo-doc-suggestion-${doc.id}`}>
      <MagicWand className="w-3.5 h-3.5 text-purple-700 flex-shrink-0 mt-0.5" weight="fill" />
      <div className="flex-1 min-w-0 text-[11.5px] text-foreground">
        <div>
          <span className="text-muted">La IA sugiere mover a </span>
          <b className="text-purple-900">{target?.label || doc.suggested_folder}</b>
          <span className={`ml-1.5 font-semibold ${confColor}`}>
            ({conf}% confianza)
          </span>
        </div>
        {doc.suggested_reason && (
          <div className="text-[10.5px] text-muted italic mt-0.5 line-clamp-2">
            {doc.suggested_reason}
          </div>
        )}
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={onAccept}
                data-testid={`repo-doc-suggestion-accept-${doc.id}`}
                title={`Mover a ${target?.label || doc.suggested_folder}`}
                className="bg-purple-600 hover:bg-purple-700 text-white text-[10.5px] h-7 px-2 rounded inline-flex items-center gap-1 font-semibold">
          <Check className="w-3 h-3" weight="bold" /> Aceptar
        </button>
        <button onClick={onDismiss}
                data-testid={`repo-doc-suggestion-dismiss-${doc.id}`}
                title="Descartar sugerencia"
                className="text-muted hover:text-foreground text-[10.5px] h-7 px-2 rounded inline-flex items-center gap-1">
          <X className="w-3 h-3" weight="bold" />
        </button>
      </div>
    </div>
  );
}

function BulkActionsBar({ docs, selectedIds, onToggleAll, onClear, onMove, moving }) {
  const [showMove, setShowMove] = useState(false);
  const count = selectedIds.size;
  const allSelected = docs.length > 0 && docs.every((d) => selectedIds.has(d.id));
  return (
    <div className={`bg-white border rounded-md px-3 py-2 flex items-center gap-2 flex-wrap text-[12px] ${
            count > 0 ? "border-amber-400 bg-amber-50/40" : "border-border"}`}
         data-testid="repo-bulk-actions-bar">
      <button onClick={onToggleAll}
              data-testid="repo-bulk-select-all"
              className="inline-flex items-center gap-1.5 text-foreground hover:text-amber-700 font-semibold">
        {allSelected ? <CheckSquare className="w-4 h-4 text-amber-600" weight="fill" /> : <Square className="w-4 h-4" weight="regular" />}
        {allSelected ? "Deseleccionar todos" : "Seleccionar visibles"}
      </button>
      {count > 0 ? (
        <>
          <span className="text-muted">·</span>
          <span className="text-amber-700 font-bold" data-testid="repo-bulk-count">
            {count} seleccionado{count === 1 ? "" : "s"}
          </span>
          <div className="ml-auto flex items-center gap-2 relative">
            <button onClick={() => setShowMove((v) => !v)}
                    disabled={moving}
                    data-testid="repo-bulk-move-btn"
                    className="btn-brand text-[11.5px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold disabled:opacity-50">
              <ArrowsLeftRight className="w-3.5 h-3.5" weight="bold" />
              {moving ? "Moviendo…" : "Mover a…"}
            </button>
            {showMove && (
              <div className="absolute right-0 top-[110%] bg-white border border-border rounded-md shadow-lg py-1 w-56 z-20"
                   data-testid="repo-bulk-move-menu">
                {FOLDER_DEFS.map((f) => (
                  <button key={f.k}
                          onClick={() => { setShowMove(false); onMove(f.k); }}
                          data-testid={`repo-bulk-move-${f.k}`}
                          className="w-full px-3 py-1.5 text-left text-[12px] inline-flex items-center gap-2 hover:bg-amber-50">
                    <f.icon className="w-3.5 h-3.5 text-amber-600" weight="fill" />
                    {f.label}
                  </button>
                ))}
              </div>
            )}
            <button onClick={onClear}
                    data-testid="repo-bulk-clear"
                    className="text-muted hover:text-foreground text-[11.5px] inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" weight="bold" /> Limpiar
            </button>
          </div>
        </>
      ) : (
        <span className="text-muted">
          Marca varios documentos para mover en lote a otra categoría.
        </span>
      )}
    </div>
  );
}

function BulkUploadModal({ aseguradoraId, defaultFolder, onClose, onUploaded }) {
  const [files, setFiles] = useState([]);    // [{file, status, error}]
  const [folder, setFolder] = useState(defaultFolder || "sin_clasificar");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (list.length === 0) return;
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of list) {
        if (merged.length >= MAX_BULK_FILES) {
          toast.error(`Máximo ${MAX_BULK_FILES} archivos por subida`);
          break;
        }
        // Dedup por nombre + tamaño
        if (merged.some((x) => x.file.name === f.name && x.file.size === f.size)) continue;
        merged.push({ file: f, status: "pending" });
      }
      return merged;
    });
  };

  const removeAt = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (files.length === 0) { toast.error("Añade al menos un archivo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f.file));
      fd.append("folder", folder);
      if (tags) fd.append("tags", tags);
      const r = await api.post(
        `/aseguradoras/${aseguradoraId}/repositorio/upload/bulk`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const ok = r.data?.uploaded_count || 0;
      const ko = r.data?.failed_count || 0;
      if (ok > 0 && ko === 0) {
        toast.success(`${ok} documento(s) subidos · indexando en segundo plano`);
      } else if (ok > 0 && ko > 0) {
        toast.warning(`${ok} subidos OK · ${ko} fallaron: ${(r.data?.failed || []).map((f) => f.filename).join(", ")}`);
      } else {
        toast.error(`Todos los archivos fallaron: ${(r.data?.failed || []).map((f) => f.error).join("; ")}`);
      }
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo los documentos");
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="repo-bulk-upload-modal">
        <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold">Subir documentos en lote ({files.length}/{MAX_BULK_FILES})</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            data-testid="repo-bulk-dropzone"
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
                drag ? "border-amber-500 bg-amber-50" : "border-border hover:border-amber-400 hover:bg-amber-50/40"}`}>
            <UploadSimple className="w-8 h-8 mx-auto mb-1 text-amber-600" weight="fill" />
            <div className="text-[13px] font-semibold text-foreground">
              Arrastra archivos aquí o haz clic para seleccionar
            </div>
            <div className="text-[11px] text-muted mt-0.5">
              PDF, DOCX, TXT, MD · máx 50 MB por archivo · {MAX_BULK_FILES} archivos por tanda
            </div>
            <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.md"
                   onChange={(e) => addFiles(e.target.files)}
                   data-testid="repo-bulk-file-input"
                   className="hidden" />
          </div>

          {/* Lista de archivos seleccionados */}
          {files.length > 0 && (
            <div className="border border-border rounded-md max-h-52 overflow-auto divide-y divide-border"
                 data-testid="repo-bulk-file-list">
              {files.map((f, i) => (
                <div key={`${f.file.name}-${f.file.size}-${i}`} className="flex items-center gap-2 px-3 py-1.5 text-[12px]">
                  <IconForFile filename={f.file.name} />
                  <span className="flex-1 truncate" title={f.file.name}>{f.file.name}</span>
                  <span className="text-muted text-[10.5px]">{fmtSize(f.file.size)}</span>
                  <button onClick={() => removeAt(i)}
                          data-testid={`repo-bulk-file-remove-${i}`}
                          className="text-muted hover:text-rose-600">
                    <X className="w-3.5 h-3.5" weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Categoría y tags por defecto */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-semibold text-muted block mb-1">Carpeta destino</span>
              <select value={folder} onChange={(e) => setFolder(e.target.value)}
                      className="w-full text-[12px]"
                      data-testid="repo-bulk-folder-select">
                {FOLDER_DEFS.map((f) => <option key={f.k} value={f.k}>{f.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-muted block mb-1">Etiquetas comunes</span>
              <input value={tags} onChange={(e) => setTags(e.target.value)}
                     placeholder="hogar, 2026"
                     className="w-full text-[12px]"
                     data-testid="repo-bulk-tags-input" />
            </label>
          </div>
          <div className="text-[10.5px] text-muted italic bg-blue-50 border border-blue-200 rounded-md p-2">
            <Sparkle className="w-3 h-3 inline mr-1" weight="fill" />
            Puedes dejarlo en <b>Sin clasificar</b> y categorizar después usando la selección múltiple.
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="button" onClick={submit} disabled={busy || files.length === 0}
                  data-testid="repo-bulk-upload-submit"
                  className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <UploadSimple className="w-3 h-3" weight="bold" /> {busy ? "Subiendo…" : `Subir ${files.length} archivo${files.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ aseguradoraId, doc, onClose, onSaved }) {
  const [title, setTitle] = useState(doc.title || "");
  const [folder, setFolder] = useState(doc.folder || "otros");
  const [subfolder, setSubfolder] = useState(doc.subfolder || "");
  const [tags, setTags] = useState((doc.tags || []).join(", "));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/aseguradoras/${aseguradoraId}/repositorio/${doc.id}`, {
        title: title || undefined,
        folder, subfolder: subfolder || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Documento actualizado");
      onSaved();
    } catch (e) { toast.error("Error guardando"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="repo-edit-modal">
        <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold">Editar documento</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Título</span><input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full text-[12px]" /></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Carpeta</span>
            <select value={folder} onChange={(e) => setFolder(e.target.value)} className="w-full text-[12px]">
              {FOLDER_DEFS.map((f) => <option key={f.k} value={f.k}>{f.label}</option>)}
            </select></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Subcarpeta (opcional)</span>
            <input value={subfolder} onChange={(e) => setSubfolder(e.target.value)} placeholder="ej. 2026 / Q1" className="w-full text-[12px]" /></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Etiquetas (separadas por coma)</span>
            <input value={tags} onChange={(e) => setTags(e.target.value)} className="w-full text-[12px]" /></label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="submit" disabled={busy} className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50">{busy ? "Guardando…" : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

function Citation({ c }) {
  return (
    <div className="text-[10.5px] bg-slate-50 border border-slate-200 rounded p-2 hover:bg-amber-50/40" data-testid={`citation-${c.doc_id}`}>
      <div className="flex items-center gap-1.5 font-semibold text-foreground">
        <FileText className="w-3 h-3 text-amber-700" weight="fill" />
        <span className="truncate">{c.doc_title}</span>
        {c.page_number && <span className="text-muted">pág. {c.page_number}</span>}
      </div>
      <div className="text-muted mt-1 line-clamp-2">{c.snippet}</div>
    </div>
  );
}

function SearchPanel({ aseguradoraId }) {
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/aseguradoras/${aseguradoraId}/repositorio/search`, {
        query: q, folder: folder || null, top_k: 8,
      });
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error en la búsqueda"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="repo-search-panel">
      <form onSubmit={run} className="bg-white border border-border rounded-md p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 inline-flex items-center gap-1">
          <Sparkle className="w-3 h-3" weight="fill" /> Pregunta lo que quieras a la documentación
        </div>
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ej. ¿Cuál es la franquicia de aguas en hogar 2026?" autoFocus data-testid="repo-search-input" className="flex-1 text-[13px]" />
          <select value={folder} onChange={(e) => setFolder(e.target.value)} className="text-[11px]" data-testid="repo-search-folder">
            <option value="">Todas las carpetas</option>
            {FOLDER_DEFS.map((f) => <option key={f.k} value={f.k}>{f.label}</option>)}
          </select>
          <button type="submit" disabled={busy} className="btn-brand text-[12px] px-3 py-1.5 rounded-md font-semibold inline-flex items-center gap-1" data-testid="repo-search-submit">
            <MagnifyingGlass className="w-3.5 h-3.5" weight="bold" /> {busy ? "Buscando…" : "Buscar"}
          </button>
        </div>
      </form>

      {result && (
        <div className="space-y-3">
          {result.answer ? (
            <div className="bg-white border-l-4 border-amber-500 border border-border rounded-md p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1.5 inline-flex items-center gap-1">
                <Sparkle className="w-3 h-3" weight="fill" /> Respuesta IA
              </div>
              <div className="text-[13px] leading-relaxed whitespace-pre-line text-foreground" data-testid="repo-search-answer">{result.answer}</div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[12px] text-amber-900">No se ha generado respuesta IA (modo retrieval).</div>
          )}
          {result.citations?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted mb-1.5 inline-flex items-center gap-1">
                <CaretRight className="w-3 h-3" weight="bold" /> Fuentes ({result.citations.length})
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {result.citations.map((c, i) => <Citation key={`${c.doc_id || c.doc_title || "c"}-${c.page_number || i}`} c={c} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChatPanel({ aseguradoraId, aseguradoraName }) {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history]);

  const send = async (e) => {
    e?.preventDefault?.();
    const msg = input.trim();
    if (!msg) return;
    setInput("");
    const userTurn = { id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role: "user", content: msg };
    setHistory((h) => [...h, userTurn]);
    setBusy(true);
    try {
      const { data } = await api.post(`/aseguradoras/${aseguradoraId}/repositorio/chat`, {
        message: msg, session_id: sessionId || undefined,
      });
      if (!sessionId) setSessionId(data.session_id);
      setHistory((h) => [...h, { id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role: "assistant", content: data.answer, citations: data.citations }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en chat");
      setHistory((h) => [...h, { id: `e-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role: "assistant", content: "[Error en la consulta]" }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-md flex flex-col h-[600px]" data-testid="repo-chat-panel">
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
        <ChatCircleDots className="w-4 h-4 text-amber-600" weight="fill" />
        <div className="font-display font-bold text-[13px]">Chat con la documentación de {aseguradoraName || "esta aseguradora"}</div>
        {sessionId && <span className="text-[10px] text-muted ml-auto">sesión {sessionId.slice(0, 8)}</span>}
      </div>
      <div className="flex-1 overflow-auto p-4 space-y-3">
        {history.length === 0 && (
          <div className="text-center text-muted text-sm py-12">
            <ChatCircleDots className="w-12 h-12 mx-auto mb-2 text-muted/60" weight="duotone" />
            Haz una pregunta sobre la documentación cargada y la IA te responderá citando los documentos relevantes.
          </div>
        )}
        {history.map((m, i) => (
          <div key={m.id || `msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-[13px] ${m.role === "user" ? "bg-amber-600 text-white" : "bg-slate-50 border border-border text-foreground"}`} data-testid={`repo-chat-msg-${i}`}>
              <div className="whitespace-pre-line leading-relaxed">{m.content}</div>
              {m.citations?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-amber-200/50 space-y-1">
                  {m.citations.map((c, j) => (
                    <div key={`${m.id || i}-cit-${c.doc_id || c.doc_title || j}-${c.page_number || j}`} className="text-[10px] text-muted flex items-center gap-1">
                      <FileText className="w-2.5 h-2.5" weight="fill" />
                      {c.doc_title}{c.page_number ? ` pág. ${c.page_number}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="text-[11px] text-muted italic px-3">Pensando…</div>}
        <div ref={endRef}></div>
      </div>
      <form onSubmit={send} className="px-3 py-2 border-t border-border flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Pregunta a la IA…" disabled={busy} data-testid="repo-chat-input" className="flex-1 text-[13px]" />
        <button type="submit" disabled={busy || !input.trim()} className="btn-brand text-[12px] px-3 py-1.5 rounded-md font-semibold disabled:opacity-50" data-testid="repo-chat-send">Enviar</button>
      </form>
    </div>
  );
}
